import { FeatureCollection, Geometry } from 'geojson';
import _ from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { LayerColumn } from 'uf-api';

import { useListFavoriteLayersQuery } from 'uf-api-rtk/store/Api';
import { FunctionParams } from 'uf/base/types';
import {
  DataState,
  getData,
  isLoaded,
  useDataSuspense,
} from 'uf/data/dataState';
import { makeReduxDataKey } from 'uf/data/helpers';
import { loadLayerData } from 'uf/explore/actions/layers';
import { makeGetFilterListItemColumns } from 'uf/explore/selectors/explore';
import { makeGetLayerFilters } from 'uf/explore/selectors/filters';
import { LayerId } from 'uf/layers';
import { loadLayerBounds } from 'uf/layers/actions/bounds';
import { loadLayerBreaks } from 'uf/layers/actions/breaks';
import { loadLayerGeojson } from 'uf/layers/actions/geojson';
import { loadLayerMetadata } from 'uf/layers/actions/metadata';
import { ensureLayerStats, loadLayerStats } from 'uf/layers/actions/stats';
import { makeLayerBoundsKey } from 'uf/layers/bounds';
import { makeBreaksKey } from 'uf/layers/breaks';
import {
  StatsParams,
  getLayerDataSearchKey,
  getStatsSearchKey,
} from 'uf/layers/filters';
import { getUfGeometryType } from 'uf/layers/helpers';
import { makeGetLayerBounds } from 'uf/layers/selectors/bounds';
import { makeGetLayerBreaksByKey } from 'uf/layers/selectors/breaks';
import { makeGetLayerDataState } from 'uf/layers/selectors/data';
import { makeGetGeojsonByKey } from 'uf/layers/selectors/geojson';
import { makeGetLayerMetadataState } from 'uf/layers/selectors/metadata';
import {
  makeGetLayerStatsByKey,
  makeGetLayerStatsState,
} from 'uf/layers/selectors/stats';
import { makeGetLayerVersion } from 'uf/layers/selectors/versions';
import { ProjectId } from 'uf/projects';
import { makeUseEnsure } from 'uf/ui/data/useEnsure';
import { useCurrentUser } from 'uf/ui/user/hooks';

export const useLayerStats = makeUseEnsure(
  loadLayerStats,
  makeGetLayerStatsByKey,
  getStatsSearchKey,
  layerId => !!layerId,
);

type StatsRowParams = Omit<StatsParams, 'columns'>;

export function useLayerRowStats(
  layerId: LayerId,
  queryParams: StatsRowParams,
) {
  const newQueryParams: StatsParams = {
    ...queryParams,
    columns: [],
    row_count: true,
  };
  return useLayerStats(layerId, newQueryParams);
}

type StatsColumnParams = Omit<StatsParams, 'row_count'>;

export function useLayerColumnStats(
  layerId: LayerId,
  queryParams: StatsColumnParams,
) {
  if (queryParams.columns.length === 0) {
    throw new Error(
      'useLayerColumnsStats must contain at least one valid column value',
    );
  }
  const newQueryParams: StatsParams = { ...queryParams, row_count: false };
  return useLayerStats(layerId, newQueryParams);
}

/**
 * Generic geojson call, used for casting later
 */
const useLayerGeojsonGeneric = makeUseEnsure(
  loadLayerGeojson,
  makeGetGeojsonByKey,
  getLayerDataSearchKey,
  layerId => !!layerId,
);

/**
 * Request layer geojson
 */
export function useLayerGeojson<
  G extends Geometry = Geometry,
  P extends Record<string, any> = Record<string, any>,
>(...args: FunctionParams<typeof useLayerGeojsonGeneric>) {
  return useLayerGeojsonGeneric(...args) as DataState<FeatureCollection<G, P>>;
}

// CONSIDER: the name implies you get a value, instead you get a "state" object
export const useLayerMetadata = makeUseEnsure(
  loadLayerMetadata,
  makeGetLayerMetadataState,
  layerId => ({ layerId }),
  layerId => !!layerId,
);

/**
 * useLayerMetadataValue will trigger a suspense until the data is loaded
 * and will raise an exception if a fetch error occurrs
 * @param layerId
 * @returns LayerMetadata
 */
export function useLayerMetadataValue(layerId: LayerId) {
  return useDataSuspense(useLayerMetadata(layerId));
}

export function useLayerGeometryType(layerId: LayerId) {
  const layerMetadataState = useLayerMetadata(layerId);
  const geometryType = isLoaded(layerMetadataState)
    ? getUfGeometryType(getData(layerMetadataState))
    : null;
  return geometryType;
}

export const useLayerBounds = makeUseEnsure(
  loadLayerBounds,
  makeGetLayerBounds,
  makeLayerBoundsKey,
  params => !!params?.layerId,
);

export const useLayerBreaks = makeUseEnsure(
  loadLayerBreaks,
  makeGetLayerBreaksByKey,
  makeBreaksKey,
  (layerId, layerVersion, columnKey, breakCount, breakType) =>
    !!(layerId && columnKey && breakCount && breakType !== undefined),
);

/** Get raw data from the `get_layer` call */
export const useLayerData = makeUseEnsure(
  loadLayerData,
  makeGetLayerDataState,
  (layerId, query) => {
    return { searchKey: `search:${makeReduxDataKey(layerId, query)}`, layerId };
  },
  (layerId, query, layerMetadata) => layerId && !_.isEmpty(layerMetadata),
);

export function useFavoriteLayers() {
  const user = useCurrentUser();
  const userKey = user?.key;
  const { data: entries, refetch } = useListFavoriteLayersQuery({ userKey });
  return {
    favoriteLayers: entries || [],
    refetch,
  };
}

export const useIsFilterApplied = (projectId: ProjectId, layerId: LayerId) => {
  // figure out if a filter is being applied, yes with all of this
  const getLayerStatsState = makeGetLayerStatsState();
  const getLayerFilters = makeGetLayerFilters();
  const getFilterListItemColumns = makeGetFilterListItemColumns();
  const filteredStateValue = useSelector(state =>
    getLayerFilters(state, {
      projectId,
      layerId,
      parentLayerId: null,
    }),
  );
  const filterListItemColumns = useSelector(state =>
    getFilterListItemColumns(state, {
      projectId,
      layerId,
      parentLayerId: null,
    }),
  );
  const layerStatsState = useSelector(state =>
    getLayerStatsState(state, {
      layerId,
      columns: filterListItemColumns,
      parentLayerId: null,
    }),
  );

  // ensure the layer stats data is loaded
  const getLayerVersion = makeGetLayerVersion();
  const version = useSelector(state => getLayerVersion(state, { layerId }));
  ensureLayerStats(layerId, { version, columns: filterListItemColumns });
  const layerStats = getData(layerStatsState);
  const hasFilterApplied = useMemo(() => {
    /*
     * Check if a filter is applied to decide to show percentages on filter rows or not.
     */

    // if layer is joined, always show percentages
    if (!_.isEmpty(filteredStateValue?.layers)) {
      return true;
    }
    // if no columns default to false, starting state of app
    if (!filteredStateValue?.columns) {
      return false;
    }

    const filteredStateColumnArray = Object.keys(filteredStateValue.columns);
    return filteredStateColumnArray.some(columnKey => {
      const column = filteredStateValue.columns[columnKey];
      if (column.columnMetatype === LayerColumn.MetatypeEnum.Categorical) {
        // if cetegorical column, check if filterValue array is not empty
        return column.filterValue.length > 0;
      } else if (column.columnMetatype === LayerColumn.MetatypeEnum.Numeric) {
        // if numeric compare values in layerStats to filtered values
        const layerStatsColumn = layerStats?.column_stats?.find(
          ({ column_key: statsColumnKey }) => statsColumnKey === columnKey,
        );
        // typing was not being picked up as I assume it should, always have the easy out
        const columnFilterValue = column?.filterValue as any;
        // if any value is mismatched return true
        return (
          layerStatsColumn?.numeric?.min !== columnFilterValue?.min ||
          layerStatsColumn?.numeric?.max !== columnFilterValue?.max
        );
      }
    });
  }, [filteredStateValue, layerStats]);
  return hasFilterApplied;
};
